// Per-unit-type visual styles: part-pool selectors, precomputed team colors, weapon specs,
// and the bespoke Mobile Ballista model. Fully procedural (no asset files, plan §29.4).

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { PlayerId } from '../content/types';

/** Team Blue uses blue/cyan; Team Gold uses gold/orange. */
export const OWNER_COLORS: [number, number, number, number] = [0x3b82f6, 0x22c1c3, 0xf59e0b, 0xef5a29];

export interface WeaponSpec {
  len: number;
  r: number;
  /** resting pitch forward in radians */
  tilt: number;
  color: THREE.Color;
  /** dedicated silhouette for ranged weapons; omitted keeps the shared blade/staff shape */
  kind?: 'bow' | 'crossbow' | 'rifle';
}

export interface UnitStyle {
  helm: 'dome' | 'spike' | 'hat' | null;
  helmColor: THREE.Color;
  /** body color per owner (type hue dominant, light team tint) */
  bodyColors: [THREE.Color, THREE.Color, THREE.Color, THREE.Color];
  headColor: THREE.Color;
  weapon?: WeaponSpec;
  shield: 'tower' | 'heater' | null;
  shieldColors?: [THREE.Color, THREE.Color, THREE.Color, THREE.Color];
  /** member 0 carries an owner-colored back banner */
  banner: boolean;
}

function mix(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}

function perOwner(base: number, t: number): [THREE.Color, THREE.Color, THREE.Color, THREE.Color] {
  return OWNER_COLORS.map((color) => mix(base, color, t)) as [THREE.Color, THREE.Color, THREE.Color, THREE.Color];
}

const SKIN = new THREE.Color(0xc99b72);

export const STYLES: Record<string, UnitStyle> = {
  'u-ic-worker': {
    helm: 'hat',
    helmColor: new THREE.Color(0xc2a45c),
    bodyColors: perOwner(0xd9c37e, 0.25),
    headColor: SKIN,
    shield: null,
    banner: false,
  },
  'u-ic-vanguard': {
    helm: 'dome',
    helmColor: new THREE.Color(0xbfc7d2),
    bodyColors: perOwner(0x8fa8c8, 0.15),
    headColor: SKIN,
    weapon: { len: 1.0, r: 0.05, tilt: 0.5, color: new THREE.Color(0xd7dce2) },
    shield: 'heater',
    shieldColors: perOwner(0x415a83, 0.45),
    banner: true,
  },
  'u-ic-shield-legion': {
    helm: 'dome',
    helmColor: new THREE.Color(0x7d8698),
    bodyColors: perOwner(0x6f87b8, 0.15),
    headColor: SKIN,
    weapon: { len: 0.7, r: 0.05, tilt: 0.6, color: new THREE.Color(0xd7dce2) },
    shield: 'tower',
    shieldColors: perOwner(0x4a566e, 0.6),
    banner: true,
  },
  'u-ic-pike-wardens': {
    helm: 'spike',
    helmColor: new THREE.Color(0xb08d55),
    bodyColors: perOwner(0xb8985f, 0.15),
    headColor: SKIN,
    weapon: { len: 2.4, r: 0.045, tilt: 0.35, color: new THREE.Color(0xc9b98a) },
    shield: null,
    banner: true,
  },
  // ballista: members 1-2 are crew (these style fields); member 0 is the bespoke machine
  'u-ic-mobile-ballista': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0xc8c0a8, 0.2),
    headColor: SKIN,
    shield: null,
    banner: false,
  },
  'u-ic-longshot-snipers': {
    helm: 'hat',
    helmColor: new THREE.Color(0x4f5a44),
    bodyColors: perOwner(0x76825e, 0.15),
    headColor: SKIN,
    // long thin rifle held low and level
    weapon: { len: 1.5, r: 0.032, tilt: 1.25, color: new THREE.Color(0x3a3f46), kind: 'rifle' },
    shield: null,
    banner: false,
  },
  // ironclad tank: entirely bespoke machine (no instanced parts)
  'u-ic-ironclad-tank': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x7e8894, 0.2),
    headColor: SKIN,
    shield: null,
    banner: false,
  },
  'u-ic-banner-captain': {
    helm: 'dome',
    helmColor: new THREE.Color(0xd8c07a),
    bodyColors: perOwner(0xd8c07a, 0.2),
    headColor: SKIN,
    weapon: { len: 0.9, r: 0.05, tilt: 0.5, color: new THREE.Color(0xd7dce2) },
    shield: 'heater',
    shieldColors: perOwner(0xe5e1d5, 0.42),
    banner: true,
  },
  // master builder: renders through the worker path with a gilded look
  'u-ic-master-builder': {
    helm: 'hat',
    helmColor: new THREE.Color(0xe0b84f),
    bodyColors: perOwner(0xe8d090, 0.2),
    headColor: SKIN,
    shield: null,
    banner: false,
  },
  'u-ic-crossbow-retinue': {
    helm: 'dome',
    helmColor: new THREE.Color(0x8a8068),
    bodyColors: perOwner(0xa39b7a, 0.15),
    headColor: SKIN,
    weapon: { len: 0.7, r: 0.04, tilt: 1.2, color: new THREE.Color(0x6b5a3e), kind: 'crossbow' },
    shield: null,
    banner: false,
  },
  'u-ic-royal-scouts': {
    helm: 'hat',
    helmColor: new THREE.Color(0xb09a50),
    bodyColors: perOwner(0xc9b168, 0.15),
    headColor: SKIN,
    weapon: { len: 0.6, r: 0.035, tilt: 1.1, color: new THREE.Color(0x7a6a4a) },
    shield: null,
    banner: true,
  },
  'u-ic-forge-clerics': {
    helm: 'dome',
    helmColor: new THREE.Color(0xb87a3a),
    bodyColors: perOwner(0x8a6a4a, 0.15),
    headColor: SKIN,
    weapon: { len: 0.9, r: 0.05, tilt: 0.3, color: new THREE.Color(0xd88f4a) },
    shield: null,
    banner: false,
  },
  'u-ic-iron-cavalry': {
    helm: 'spike',
    helmColor: new THREE.Color(0x8a9aac),
    bodyColors: perOwner(0x7a92a8, 0.15),
    headColor: SKIN,
    weapon: { len: 1.4, r: 0.045, tilt: 0.35, color: new THREE.Color(0xb0b8c4) },
    shield: null,
    banner: false,
  },
  'u-ic-oathbreaker-hunters': {
    helm: 'dome',
    helmColor: new THREE.Color(0x252932),
    bodyColors: perOwner(0x2f343d, 0.12),
    headColor: new THREE.Color(0xc8ced8),
    weapon: { len: 1.0, r: 0.04, tilt: 0.15, color: new THREE.Color(0xc0c8d4) },
    shield: null,
    banner: false,
  },
  'u-ic-royal-artificers': {
    helm: 'hat',
    helmColor: new THREE.Color(0xe0c060),
    bodyColors: perOwner(0xc8a878, 0.2),
    headColor: SKIN,
    weapon: { len: 0.6, r: 0.045, tilt: 0.1, color: new THREE.Color(0xc8a878) },
    shield: null,
    banner: false,
  },
  'u-ic-bastion-golems': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x8c8478, 0.2),
    headColor: new THREE.Color(0xd8b04a),
    shield: 'tower',
    shieldColors: perOwner(0x6a6258, 0.5),
    banner: false,
  },
  'u-ic-gryphon-lancers': {
    helm: 'spike',
    helmColor: new THREE.Color(0xf4f0e0),
    bodyColors: perOwner(0xe8dcb0, 0.15),
    headColor: SKIN,
    weapon: { len: 1.5, r: 0.04, tilt: 0.45, color: new THREE.Color(0xe8dcb0) },
    shield: null,
    banner: true,
  },
  'u-ic-fortress-ram': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x6a6258, 0.2),
    headColor: new THREE.Color(0xd8b04a),
    weapon: { len: 1.2, r: 0.08, tilt: 1.35, color: new THREE.Color(0xd8b04a) },
    shield: 'tower',
    shieldColors: perOwner(0x565048, 0.5),
    banner: false,
  },
  'u-ic-kings-exemplars': {
    helm: 'spike',
    helmColor: new THREE.Color(0xf0d060),
    bodyColors: perOwner(0xf0d060, 0.15),
    headColor: SKIN,
    weapon: { len: 1.1, r: 0.05, tilt: 0.4, color: new THREE.Color(0xf0d060) },
    shield: null,
    banner: true,
  },
  // reserve cohort: deliberately humbler than u-ic-shield-legion — plain steel-blue,
  // short sword, heater instead of tower shield, and no banner (they're the reserves)
  'u-ic-reserve-cohort': {
    helm: 'dome',
    helmColor: new THREE.Color(0x99a1ad),
    bodyColors: perOwner(0x8494aa, 0.15),
    headColor: SKIN,
    weapon: { len: 0.85, r: 0.05, tilt: 0.5, color: new THREE.Color(0xb9bfc8) },
    shield: 'heater',
    shieldColors: perOwner(0x515c72, 0.35),
    banner: false,
  },
  // bastion wagon: bespoke rig in ironCovenantRigs.ts; entry kept for corpse/minimap tint
  'u-ic-bastion-wagon': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x7d8aa0, 0.2),
    headColor: new THREE.Color(0xd8b04a),
    shield: null,
    banner: false,
  },

  // ---- Verdant Wilds (green/bark palette; beast rigs deferred — shared parts for now)
  'u-vw-worker': {
    helm: 'hat',
    helmColor: new THREE.Color(0x7a9a4a),
    bodyColors: perOwner(0x9dc06a, 0.25),
    headColor: SKIN,
    shield: null,
    banner: false,
  },
  'u-vw-thorn-guard': {
    helm: 'spike',
    helmColor: new THREE.Color(0x5a7a3a),
    bodyColors: perOwner(0x5f9e58, 0.15),
    headColor: SKIN,
    weapon: { len: 0.9, r: 0.05, tilt: 0.55, color: new THREE.Color(0x8a6e4a) },
    shield: 'tower',
    shieldColors: perOwner(0x4a6a38, 0.5),
    banner: true,
  },
  'u-vw-vine-archers': {
    helm: 'hat',
    helmColor: new THREE.Color(0x6a8a4a),
    bodyColors: perOwner(0x7eb06a, 0.15),
    headColor: SKIN,
    weapon: { len: 1.3, r: 0.035, tilt: 1.1, color: new THREE.Color(0x6a8a4a), kind: 'bow' },
    shield: null,
    banner: false,
  },
  'u-vw-root-mender': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0xa8d08a, 0.15),
    headColor: SKIN,
    weapon: { len: 1.4, r: 0.04, tilt: 0.2, color: new THREE.Color(0x9dc06a) },
    shield: null,
    banner: false,
  },
  'u-vw-razorhorn': {
    helm: 'spike',
    helmColor: new THREE.Color(0x6a4a2a),
    bodyColors: perOwner(0x8a6e4a, 0.2),
    headColor: new THREE.Color(0x6a4e34),
    shield: null,
    banner: false,
  },
  'u-vw-canopy-stalkers': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x6a8a5a, 0.2),
    headColor: new THREE.Color(0x5a7a4a),
    shield: null,
    banner: false,
  },
  'u-vw-groveback': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x6f8a52, 0.2),
    headColor: new THREE.Color(0x5a7040),
    shield: null,
    banner: true,
  },
  'u-vw-barkwright': {
    helm: 'hat',
    helmColor: new THREE.Color(0xb8a05f),
    bodyColors: perOwner(0xb8a05f, 0.2),
    headColor: SKIN,
    shield: null,
    banner: false,
  },
  'u-vw-canopy-riders': {
    helm: 'hat',
    helmColor: new THREE.Color(0x8ab86a),
    bodyColors: perOwner(0x8ab86a, 0.15),
    headColor: SKIN,
    weapon: { len: 1.4, r: 0.035, tilt: 1.15, color: new THREE.Color(0x5a7a3a) },
    shield: null,
    banner: false,
  },
  'u-vw-sproutling-swarm': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0xc4de8f, 0.15),
    headColor: new THREE.Color(0xa8c87a),
    shield: null,
    banner: false,
  },
  'u-vw-sporecallers': {
    helm: 'hat',
    helmColor: new THREE.Color(0x8a6ca0),
    bodyColors: perOwner(0x9d86b8, 0.15),
    headColor: SKIN,
    weapon: { len: 1.2, r: 0.04, tilt: 0.25, color: new THREE.Color(0x7a5c8f) },
    shield: null,
    banner: false,
  },
  'u-vw-briarback-wardens': {
    helm: 'spike',
    helmColor: new THREE.Color(0x5c4426),
    bodyColors: perOwner(0x7a5c38, 0.15),
    headColor: SKIN,
    shield: 'tower',
    shieldColors: perOwner(0x5c4a30, 0.5),
    banner: false,
  },
  'u-vw-moonfang-hunters': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0xb9c8cf, 0.15),
    headColor: new THREE.Color(0x9aa8b0),
    shield: null,
    banner: false,
  },
  'u-vw-skybloom-drakes': {
    helm: 'spike',
    helmColor: new THREE.Color(0xb87a98),
    bodyColors: perOwner(0xd08fae, 0.15),
    headColor: new THREE.Color(0xb87a98),
    shield: null,
    banner: false,
  },
  'u-vw-ancient-treants': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x5c4a33, 0.2),
    headColor: new THREE.Color(0x6e5a40),
    weapon: { len: 1.6, r: 0.06, tilt: 0.3, color: new THREE.Color(0x5c4a33) },
    shield: null,
    banner: false,
  },
  'u-vw-thornmaw-hydra': {
    helm: 'spike',
    helmColor: new THREE.Color(0x6e8a2a),
    bodyColors: perOwner(0x94b83c, 0.2),
    headColor: new THREE.Color(0x7a9a30),
    shield: null,
    banner: false,
  },
  'u-vw-sunpetal-seraphs': {
    helm: 'dome',
    helmColor: new THREE.Color(0xf2dc8a),
    bodyColors: perOwner(0xe8c95a, 0.15),
    headColor: SKIN,
    weapon: { len: 0.8, r: 0.035, tilt: 0.0, color: new THREE.Color(0xf2dc8a) },
    shield: null,
    banner: true,
  },
  'u-vw-verdant-behemoth': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x54702e, 0.2),
    headColor: new THREE.Color(0x465e26),
    shield: null,
    banner: true,
  },
  // rootwarden/seedback: bespoke annex rigs in factionSquadRigs.ts; entries keep corpse tint
  'u-vw-rootwarden': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x6a8f4e, 0.15),
    headColor: new THREE.Color(0x54703a),
    shield: null,
    banner: false,
  },
  'u-vw-seedback': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x8a9a52, 0.2),
    headColor: new THREE.Color(0x6a7a40),
    shield: null,
    banner: false,
  },

  // ---- Ashen Host (bone/ember palette)
  'u-ah-worker': {
    helm: 'hat',
    helmColor: new THREE.Color(0x5a5560),
    bodyColors: perOwner(0x8a8090, 0.25),
    headColor: new THREE.Color(0xb0a8a0),
    shield: null,
    banner: false,
  },
  'u-ah-shade': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x77d97e, 0.1),
    headColor: new THREE.Color(0x9fe8b5),
    shield: null,
    banner: false,
  },
  'u-ah-reavers': {
    helm: 'spike',
    helmColor: new THREE.Color(0x4a4550),
    bodyColors: perOwner(0x6a5d70, 0.15),
    headColor: new THREE.Color(0xb0a8a0),
    weapon: { len: 0.9, r: 0.05, tilt: 0.55, color: new THREE.Color(0x8a8590) },
    shield: null,
    banner: true,
  },
  'u-ah-ghoul-pack': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x7a6d80, 0.15),
    headColor: new THREE.Color(0xa89aa0),
    shield: null,
    banner: false,
  },
  'u-ah-bone-archers': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0xc8c0b0, 0.15),
    headColor: new THREE.Color(0xd8d0c0),
    weapon: { len: 1.3, r: 0.035, tilt: 1.1, color: new THREE.Color(0xd8d0c0), kind: 'bow' },
    shield: null,
    banner: false,
  },
  'u-ah-corpse-carrier': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x8a7a6a, 0.2),
    headColor: new THREE.Color(0xa89a8a),
    shield: null,
    banner: false,
  },
  'u-ah-flesh-guard': {
    helm: 'dome',
    helmColor: new THREE.Color(0x7a5a5a),
    bodyColors: perOwner(0x9a6a6a, 0.15),
    headColor: new THREE.Color(0xb08a8a),
    weapon: { len: 0.8, r: 0.055, tilt: 0.6, color: new THREE.Color(0xd8d0c0) },
    shield: 'tower',
    shieldColors: perOwner(0x6a5050, 0.5),
    banner: true,
  },
  'u-ah-sacrificial-brute': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x8a5a5a, 0.2),
    headColor: new THREE.Color(0xa07a7a),
    shield: null,
    banner: false,
  },
  'u-ah-soul-collector': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x9ad8e0, 0.2),
    headColor: new THREE.Color(0xc0e8ee),
    shield: null,
    banner: false,
  },
  'u-ah-gore-engine': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x7a4a4a, 0.2),
    headColor: new THREE.Color(0x8a5a5a),
    shield: null,
    banner: true,
  },
  'u-ah-soul-lanterns': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0xd0907a, 0.15),
    headColor: new THREE.Color(0xe8c0a8),
    weapon: { len: 1.3, r: 0.035, tilt: 0.2, color: new THREE.Color(0xd06040) },
    shield: null,
    banner: false,
  },
  'u-ah-wailing-brides': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0xb8a8c8, 0.1),
    headColor: new THREE.Color(0xd8cce0),
    shield: null,
    banner: false,
  },
  'u-ah-dread-cavalry': {
    helm: 'spike',
    helmColor: new THREE.Color(0x50303a),
    bodyColors: perOwner(0x704a5a, 0.15),
    headColor: new THREE.Color(0xa89aa0),
    weapon: { len: 2.3, r: 0.045, tilt: 0.35, color: new THREE.Color(0x8a2a2a) },
    shield: null,
    banner: true,
  },
  'u-ah-bonewyrm-riders': {
    helm: 'dome',
    helmColor: new THREE.Color(0x8a8570),
    bodyColors: perOwner(0xb0a890, 0.15),
    headColor: new THREE.Color(0xc8c0a8),
    weapon: { len: 1.1, r: 0.045, tilt: 0.15, color: new THREE.Color(0x9aa070) },
    shield: null,
    banner: false,
  },
  'u-ah-plague-apostles': {
    helm: 'hat',
    helmColor: new THREE.Color(0x4a5a30),
    bodyColors: perOwner(0x7a8a5a, 0.15),
    headColor: new THREE.Color(0xa8b090),
    weapon: { len: 1.4, r: 0.04, tilt: 0.2, color: new THREE.Color(0x5a6a3a) },
    shield: null,
    banner: false,
  },
  'u-ah-ebon-executioners': {
    helm: 'dome',
    helmColor: new THREE.Color(0x26262e),
    bodyColors: perOwner(0x3a3a44, 0.15),
    headColor: new THREE.Color(0x8a8590),
    weapon: { len: 1.6, r: 0.055, tilt: 0.5, color: new THREE.Color(0x202028) },
    shield: null,
    banner: false,
  },
  'u-ah-cinder-revenants': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x9a5a45, 0.15),
    headColor: new THREE.Color(0xc88a60),
    weapon: { len: 0.8, r: 0.05, tilt: 0.4, color: new THREE.Color(0xd06a30) },
    shield: null,
    banner: false,
  },
  'u-ah-grave-leviathan': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x7a7060, 0.2),
    headColor: new THREE.Color(0x8a8070),
    shield: null,
    banner: false,
  },
  'u-ah-deathless-legion': {
    helm: 'spike',
    helmColor: new THREE.Color(0x8a7a58),
    bodyColors: perOwner(0xa89a78, 0.15),
    headColor: new THREE.Color(0xc8c0b0),
    weapon: { len: 1.5, r: 0.045, tilt: 0.3, color: new THREE.Color(0x8a7a58) },
    shield: 'tower',
    shieldColors: perOwner(0x6a5e48, 0.5),
    banner: true,
  },
  // bone hearse: bespoke rig in factionSquadRigs.ts; entry kept for corpse/minimap tint
  'u-ah-bone-hearse': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x565060, 0.15),
    headColor: new THREE.Color(0xc8c0b0),
    shield: null,
    banner: false,
  },
  // conjured annex squads: quick shared-humanoid palettes — hunched grave-things,
  // crimson thralls, and raw flesh crawlers (all bannerless, all bare-headed)
  'u-ah-grave-ghouls': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x707a68, 0.1),
    headColor: new THREE.Color(0x9aa88e),
    weapon: { len: 0.5, r: 0.04, tilt: 0.2, color: new THREE.Color(0xcfc6b4) }, // bone claw
    shield: null,
    banner: false,
  },
  'u-ah-blood-thrall': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x8a3040, 0.1),
    headColor: new THREE.Color(0xb09a96),
    weapon: { len: 0.6, r: 0.045, tilt: 0.55, color: new THREE.Color(0x55232b) }, // jagged blade
    shield: null,
    banner: false,
  },
  'u-ah-flesh-crawler': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x7a4a4a, 0.1),
    headColor: new THREE.Color(0x96605c),
    weapon: { len: 0.4, r: 0.035, tilt: 0.25, color: new THREE.Color(0xcfc6b4) }, // tiny claw
    shield: null,
    banner: false,
  },

  // ---- Astral Dominion (white/cyan crystal palette; hover rigs deferred)
  'u-ad-worker': {
    helm: 'hat',
    helmColor: new THREE.Color(0x7a98c8),
    bodyColors: perOwner(0x9fb8d8, 0.25),
    headColor: new THREE.Color(0xd8e0f0),
    shield: null,
    banner: false,
  },
  'u-ad-blink-infantry': {
    helm: 'dome',
    helmColor: new THREE.Color(0xb8d0f0),
    bodyColors: perOwner(0x8fb0d8, 0.15),
    headColor: new THREE.Color(0xd8e0f0),
    weapon: { len: 1.0, r: 0.045, tilt: 0.5, color: new THREE.Color(0x6fd8e8) },
    shield: null,
    banner: true,
  },
  'u-ad-arc-snipers': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x6fd8e8, 0.15),
    headColor: new THREE.Color(0xc8e8f0),
    weapon: { len: 1.6, r: 0.03, tilt: 1.25, color: new THREE.Color(0x4a90b8) },
    shield: null,
    banner: false,
  },
  'u-ad-phase-tank': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x7a90b8, 0.2),
    headColor: new THREE.Color(0xb8c8e0),
    shield: null,
    banner: false,
  },
  'u-ad-targeting-construct': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0xb8d0e8, 0.15),
    headColor: new THREE.Color(0xe0e8f8),
    shield: null,
    banner: false,
  },
  'u-ad-shield-drone': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x8fd0e0, 0.15),
    headColor: new THREE.Color(0xd0e8f0),
    shield: 'tower',
    shieldColors: perOwner(0x6fa8c8, 0.4),
    banner: false,
  },
  'u-ad-phase-engineer': {
    helm: 'hat',
    helmColor: new THREE.Color(0xc8d8e8),
    bodyColors: perOwner(0xc8d8e8, 0.2),
    headColor: new THREE.Color(0xe0e8f8),
    shield: null,
    banner: false,
  },
  'u-ad-star-lancer': {
    helm: 'spike',
    helmColor: new THREE.Color(0xa8c8f0),
    bodyColors: perOwner(0xa8c8f0, 0.15),
    headColor: new THREE.Color(0xd8e8f8),
    weapon: { len: 2.2, r: 0.04, tilt: 0.35, color: new THREE.Color(0x6fd8e8) },
    shield: null,
    banner: false,
  },
  'u-ad-siege-orb': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x6a80c0, 0.2),
    headColor: new THREE.Color(0x9fb0d8),
    shield: null,
    banner: false,
  },
  'u-ad-rift-hounds': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x9a86d8, 0.15),
    headColor: new THREE.Color(0x8a76c8),
    shield: null,
    banner: false,
  },
  'u-ad-prism-adepts': {
    helm: 'hat',
    helmColor: new THREE.Color(0xb090e0),
    bodyColors: perOwner(0xc9a8f0, 0.15),
    headColor: new THREE.Color(0xe0d0f8),
    weapon: { len: 1.5, r: 0.035, tilt: 0.15, color: new THREE.Color(0x6fd8e8) },
    shield: null,
    banner: false,
  },
  'u-ad-null-monks': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0x8a76b8, 0.15),
    headColor: new THREE.Color(0xc8c0d8),
    weapon: { len: 0.7, r: 0.045, tilt: 1.3, color: new THREE.Color(0x2a2a34) },
    shield: null,
    banner: false,
  },
  'u-ad-comet-riders': {
    helm: 'dome',
    helmColor: new THREE.Color(0xd8b868),
    bodyColors: perOwner(0xf0cf8a, 0.15),
    headColor: new THREE.Color(0xe8dcc0),
    weapon: { len: 1.8, r: 0.045, tilt: 0.3, color: new THREE.Color(0xd8b04a) },
    shield: null,
    banner: false,
  },
  'u-ad-vector-blades': {
    helm: 'spike',
    helmColor: new THREE.Color(0x9a70cc),
    bodyColors: perOwner(0xb08ae0, 0.15),
    headColor: new THREE.Color(0xd8c8f0),
    weapon: { len: 0.8, r: 0.04, tilt: 0.75, color: new THREE.Color(0x8a5ad0) },
    shield: null,
    banner: false,
  },
  'u-ad-gravity-weavers': {
    helm: 'hat',
    helmColor: new THREE.Color(0x5a4c96),
    bodyColors: perOwner(0x6f5fae, 0.15),
    headColor: new THREE.Color(0xc8c0e0),
    weapon: { len: 1.5, r: 0.04, tilt: 0.1, color: new THREE.Color(0x4a3e86) },
    shield: null,
    banner: false,
  },
  'u-ad-celestial-wardens': {
    helm: 'dome',
    helmColor: new THREE.Color(0xd8ce9a),
    bodyColors: perOwner(0xe6ddb0, 0.15),
    headColor: new THREE.Color(0xf0e8c8),
    shield: 'tower',
    shieldColors: perOwner(0xc8be8a, 0.4),
    banner: false,
  },
  'u-ad-chrono-lancers': {
    helm: 'spike',
    helmColor: new THREE.Color(0x5ac0a8),
    bodyColors: perOwner(0x74d8c0, 0.15),
    headColor: new THREE.Color(0xc8ece0),
    weapon: { len: 2.4, r: 0.04, tilt: 0.3, color: new THREE.Color(0x4ad8c8) },
    shield: null,
    banner: true,
  },
  'u-ad-solar-ark': {
    helm: 'dome',
    helmColor: new THREE.Color(0xe8d080),
    bodyColors: perOwner(0xf8e6a0, 0.2),
    headColor: new THREE.Color(0xf8f0d0),
    weapon: { len: 1.6, r: 0.05, tilt: 0.6, color: new THREE.Color(0xf0e0a0) },
    shield: null,
    banner: true,
  },
  // prism skiff: bespoke hover rig in factionSquadRigs.ts; entry kept for corpse/minimap tint
  'u-ad-prism-skiff': {
    helm: null,
    helmColor: SKIN,
    bodyColors: perOwner(0xb8d0f0, 0.15),
    headColor: new THREE.Color(0xd8e0f0),
    shield: null,
    banner: false,
  },
};

// ---------------------------------------------------------------- part geometries
// All pre-translated to head/body height in unit-local space so the whole figure
// shares one instance matrix (lean/bob applies to body + head + helm as one piece).

export function makeHelmDomeGeo(): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(0.27, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
  g.translate(0, 1.6, 0);
  return g;
}

export function makeHelmSpikeGeo(): THREE.BufferGeometry {
  const cap = new THREE.SphereGeometry(0.27, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
  cap.translate(0, 1.6, 0);
  const spike = new THREE.ConeGeometry(0.09, 0.5, 6);
  spike.translate(0, 2.05, 0);
  return mergeGeometries([cap, spike])!;
}

export function makeHatGeo(): THREE.BufferGeometry {
  const crown = new THREE.ConeGeometry(0.3, 0.28, 10);
  crown.translate(0, 1.88, 0);
  const brim = new THREE.CylinderGeometry(0.46, 0.48, 0.05, 12);
  brim.translate(0, 1.76, 0);
  return mergeGeometries([crown, brim])!;
}

/** Back banner: pole + flag, mounted behind the squad leader (local -z = behind). */
export function makeBannerGeo(): THREE.BufferGeometry {
  const pole = new THREE.CylinderGeometry(0.03, 0.03, 1.1, 5);
  pole.translate(0, 1.65, -0.38);
  const flag = new THREE.PlaneGeometry(0.42, 0.55);
  flag.translate(0.24, 1.95, -0.38);
  return mergeGeometries([pole, flag])!;
}

export function makeTowerShieldGeo(): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(0.72, 0.95, 0.08);
  return g;
}

/** Broad Covenant heater shield with a pointed lower edge and raised central boss. */
export function makeHeaterShieldGeo(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(-0.42, 0.34);
  shape.lineTo(0.42, 0.34);
  shape.lineTo(0.38, -0.2);
  shape.lineTo(0, -0.58);
  shape.lineTo(-0.38, -0.2);
  shape.closePath();
  const plate = new THREE.ExtrudeGeometry(shape, { depth: 0.075, bevelEnabled: false, steps: 1 });
  plate.translate(0, 0.08, -0.0375);
  const boss = new THREE.CylinderGeometry(0.12, 0.12, 0.11, 10);
  boss.rotateX(Math.PI / 2);
  boss.translate(0, 0.08, 0.06);
  // ExtrudeGeometry is non-indexed while CylinderGeometry is indexed. BufferGeometryUtils
  // rejects a mixed pair and returns null, which then crashes Mesh construction in previews.
  const compatiblePlate = plate.index ? plate.toNonIndexed() : plate;
  const compatibleBoss = boss.index ? boss.toNonIndexed() : boss;
  return mergeGeometries([compatiblePlate, compatibleBoss]) ?? compatiblePlate;
}

/** Upright recurved bow with a visible string and nocked arrow; local +z is aim-forward. */
export function makeBowGeo(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const bowCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(-0.06, -0.82, 0),
    new THREE.Vector3(0.16, -0.72, 0),
    new THREE.Vector3(0.3, -0.45, 0),
    new THREE.Vector3(0.08, -0.13, 0),
    new THREE.Vector3(0.08, 0.13, 0),
    new THREE.Vector3(0.3, 0.45, 0),
    new THREE.Vector3(0.16, 0.72, 0),
    new THREE.Vector3(-0.06, 0.82, 0),
  ]);
  parts.push(new THREE.TubeGeometry(bowCurve, 36, 0.035, 6, false));

  const segment = (a: THREE.Vector3, b: THREE.Vector3, radius: number): THREE.BufferGeometry => {
    const delta = b.clone().sub(a);
    const geometry = new THREE.CylinderGeometry(radius, radius, delta.length(), 5);
    geometry.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), delta.normalize()));
    geometry.translate((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2);
    return geometry;
  };
  const nock = new THREE.Vector3(-0.13, 0, -0.1);
  parts.push(segment(new THREE.Vector3(-0.06, -0.82, 0), nock, 0.009));
  parts.push(segment(nock, new THREE.Vector3(-0.06, 0.82, 0), 0.009));
  const grip = new THREE.CylinderGeometry(0.055, 0.055, 0.24, 7);
  grip.translate(0.08, 0, 0);
  parts.push(grip);
  const arrow = new THREE.CylinderGeometry(0.018, 0.018, 1.25, 5);
  arrow.rotateX(Math.PI / 2);
  arrow.translate(-0.13, 0, 0.48);
  parts.push(arrow);
  const tip = new THREE.ConeGeometry(0.05, 0.18, 5);
  tip.rotateX(Math.PI / 2);
  tip.translate(-0.13, 0, 1.17);
  parts.push(tip);
  for (const sy of [-1, 1]) {
    const feather = new THREE.BoxGeometry(0.09, 0.025, 0.22);
    feather.translate(-0.13, sy * 0.035, -0.1);
    parts.push(feather);
  }
  return mergeGeometries(parts)!;
}

/** Stock, bow limbs, string, and loaded bolt; local +z is aim-forward. */
export function makeCrossbowGeo(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const stock = new THREE.BoxGeometry(0.11, 0.12, 1.05);
  stock.translate(0, 0, 0.25);
  parts.push(stock);
  for (const sx of [-1, 1]) {
    const limb = new THREE.CylinderGeometry(0.025, 0.04, 0.72, 6);
    limb.rotateZ(Math.PI / 2 + sx * 0.28);
    limb.translate(sx * 0.32, 0, 0.22);
    parts.push(limb);
    const string = new THREE.CylinderGeometry(0.009, 0.009, 0.58, 4);
    string.rotateZ(Math.PI / 2 - sx * 0.38);
    string.translate(sx * 0.25, 0, 0.02);
    parts.push(string);
  }
  const bolt = new THREE.CylinderGeometry(0.016, 0.016, 1.15, 5);
  bolt.rotateX(Math.PI / 2);
  bolt.translate(0, 0.05, 0.48);
  parts.push(bolt);
  const tip = new THREE.ConeGeometry(0.045, 0.16, 5);
  tip.rotateX(Math.PI / 2);
  tip.translate(0, 0.05, 1.12);
  parts.push(tip);
  return mergeGeometries(parts)!;
}

/** Long rune-rifle with wooden stock, brass scope, chamber, and folded firing support. */
export function makeRifleGeo(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const stock = new THREE.BoxGeometry(0.16, 0.2, 0.78);
  stock.translate(0, -0.02, -0.22);
  parts.push(stock);
  const receiver = new THREE.BoxGeometry(0.2, 0.18, 0.42);
  receiver.translate(0, 0.02, 0.36);
  parts.push(receiver);
  const barrel = new THREE.CylinderGeometry(0.035, 0.045, 1.25, 7);
  barrel.rotateX(Math.PI / 2);
  barrel.translate(0, 0.04, 1.0);
  parts.push(barrel);
  const muzzle = new THREE.CylinderGeometry(0.07, 0.07, 0.18, 8);
  muzzle.rotateX(Math.PI / 2);
  muzzle.translate(0, 0.04, 1.65);
  parts.push(muzzle);
  const scope = new THREE.CylinderGeometry(0.055, 0.07, 0.58, 8);
  scope.rotateX(Math.PI / 2);
  scope.translate(0, 0.2, 0.42);
  parts.push(scope);
  const chamber = new THREE.TorusGeometry(0.1, 0.03, 6, 10);
  chamber.rotateY(Math.PI / 2);
  chamber.translate(0, 0.02, 0.45);
  parts.push(chamber);
  for (const sx of [-1, 1]) {
    const rest = new THREE.CylinderGeometry(0.018, 0.025, 0.48, 5);
    rest.rotateZ(sx * 0.38);
    rest.translate(sx * 0.08, -0.22, 0.82);
    parts.push(rest);
  }
  return mergeGeometries(parts)!;
}

// ---------------------------------------------------------------- limbs
// Origin at the joint (shoulder/hip), limb extends down local -Y so rotation.x
// swings it forward/back like a pendulum. The renderer places the joint each frame.

/** Leg: thigh+shin as one tapered piece, plus a small boot. Origin at the hip. */
export function makeLegGeo(): THREE.BufferGeometry {
  const leg = new THREE.CylinderGeometry(0.085, 0.105, 0.72, 6);
  leg.translate(0, -0.36, 0);
  const boot = new THREE.BoxGeometry(0.19, 0.11, 0.3);
  boot.translate(0, -0.72, 0.05);
  return mergeGeometries([leg, boot])!;
}

/** Arm: upper+forearm as one piece with a fist. Origin at the shoulder. */
export function makeArmGeo(): THREE.BufferGeometry {
  const arm = new THREE.CylinderGeometry(0.065, 0.075, 0.58, 6);
  arm.translate(0, -0.29, 0);
  const fist = new THREE.SphereGeometry(0.085, 6, 5);
  fist.translate(0, -0.58, 0);
  return mergeGeometries([arm, fist])!;
}

/** Torso: chest capsule + pauldron bumps + waist skirt, origin at ground (0..~1.55). */
export function makeTorsoGeo(): THREE.BufferGeometry {
  const chest = new THREE.CapsuleGeometry(0.34, 0.5, 3, 10);
  chest.translate(0, 1.08, 0);
  const skirt = new THREE.CylinderGeometry(0.3, 0.4, 0.32, 8);
  skirt.translate(0, 0.68, 0);
  const pauldronL = new THREE.SphereGeometry(0.15, 7, 5);
  pauldronL.translate(-0.38, 1.34, 0);
  const pauldronR = new THREE.SphereGeometry(0.15, 7, 5);
  pauldronR.translate(0.38, 1.34, 0);
  return mergeGeometries([chest, skirt, pauldronL, pauldronR])!;
}

// ---------------------------------------------------------------- corpses

/**
 * Fallen soldier: authored standing (origin at the feet) with lolled head, splayed arms and
 * slightly bent legs. The renderer tips it about the feet (rotX 0 -> PI/2) so it topples and
 * comes to rest face-up — a cheap one-piece ragdoll that reads at RTS zoom.
 */
export function makeCorpseGeo(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const chest = new THREE.CapsuleGeometry(0.3, 0.5, 3, 8);
  chest.translate(0, 1.0, 0);
  parts.push(chest);
  const head = new THREE.SphereGeometry(0.21, 8, 6);
  head.translate(0.1, 1.5, 0.03);
  parts.push(head);
  for (const sx of [-1, 1]) {
    const leg = new THREE.CylinderGeometry(0.08, 0.1, 0.75, 6);
    leg.translate(0, 0.375, 0);
    leg.rotateZ(sx * -0.16); // splayed outward from the feet
    leg.translate(sx * 0.14, 0, 0);
    parts.push(leg);
  }
  for (const sx of [-1, 1]) {
    const arm = new THREE.CylinderGeometry(0.06, 0.07, 0.62, 6);
    arm.translate(0, -0.31, 0); // origin at the shoulder
    arm.rotateZ(sx * 1.3); // flung out to the sides
    arm.rotateY(sx * 0.3);
    arm.translate(sx * 0.26, 1.32, 0.04);
    parts.push(arm);
  }
  return mergeGeometries(parts)!;
}

/**
 * Age-of-Empires-style skeleton pile: skull, rib hoops and scattered long bones, authored
 * lying flat with the skull toward local +z (matching where the corpse's head came to rest).
 */
export function makeBonesGeo(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const skull = new THREE.SphereGeometry(0.16, 8, 6);
  skull.translate(0.06, 0.12, 0.62);
  parts.push(skull);
  const jaw = new THREE.BoxGeometry(0.14, 0.08, 0.12);
  jaw.translate(0.06, 0.05, 0.48);
  parts.push(jaw);
  // rib hoops arching over the chest line
  for (let i = 0; i < 3; i++) {
    const rib = new THREE.TorusGeometry(0.2 - i * 0.03, 0.03, 5, 8, Math.PI);
    rib.rotateY(Math.PI / 2); // arch upward, spanning across x
    rib.translate(0, 0.04, 0.18 - i * 0.16);
    parts.push(rib);
  }
  // spine + scattered long bones
  const spine = new THREE.CylinderGeometry(0.035, 0.035, 0.55, 5);
  spine.rotateX(Math.PI / 2);
  spine.translate(0, 0.03, 0.05);
  parts.push(spine);
  for (const [bx, bz, ry] of [
    [-0.24, -0.38, 0.55],
    [0.2, -0.32, -0.7],
    [0.3, 0.2, 1.2],
  ]) {
    const bone = new THREE.CylinderGeometry(0.03, 0.04, 0.6, 5);
    bone.rotateX(Math.PI / 2);
    bone.rotateY(ry);
    bone.translate(bx, 0.04, bz);
    parts.push(bone);
  }
  return mergeGeometries(parts)!;
}

/**
 * Fallen giant: same feet-origin standing authoring as makeCorpseGeo (the renderer reuses
 * the topple math), but bulked up — barrel chest, heavy shoulders, thick limbs — so bosses
 * and giant-tier units leave a body that reads at their scale instead of a soldier wedge.
 */
export function makeGiantCorpseGeo(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const chest = new THREE.CapsuleGeometry(0.52, 0.7, 4, 10);
  chest.translate(0, 1.25, 0);
  parts.push(chest);
  const gut = new THREE.SphereGeometry(0.48, 9, 7);
  gut.translate(0, 0.85, 0.06);
  parts.push(gut);
  const head = new THREE.SphereGeometry(0.3, 9, 7);
  head.translate(0.14, 1.95, 0.04);
  parts.push(head);
  for (const sx of [-1, 1]) {
    const shoulder = new THREE.SphereGeometry(0.26, 8, 6);
    shoulder.translate(sx * 0.6, 1.68, 0);
    parts.push(shoulder);
    const leg = new THREE.CylinderGeometry(0.17, 0.21, 0.95, 7);
    leg.translate(0, 0.475, 0);
    leg.rotateZ(sx * -0.18);
    leg.translate(sx * 0.26, 0, 0);
    parts.push(leg);
    const arm = new THREE.CylinderGeometry(0.13, 0.15, 0.95, 7);
    arm.translate(0, -0.475, 0);
    arm.rotateZ(sx * 1.25);
    arm.rotateY(sx * 0.25);
    arm.translate(sx * 0.5, 1.62, 0.05);
    parts.push(arm);
  }
  return mergeGeometries(parts)!;
}

/**
 * Wrecked war machine: charred broken hull with a skewed turret and bent cannon, authored
 * already grounded (no topple — the renderer gives it a small settle tilt only). One shape
 * serves tank-type units; scale/color carry the size and faction.
 */
export function makeTankWreckGeo(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const hull = new THREE.BoxGeometry(1.5, 0.55, 2.2);
  hull.rotateZ(0.09); // listing to one side
  hull.translate(0, 0.32, 0);
  parts.push(hull);
  // blown-open glacis plate
  const glacis = new THREE.BoxGeometry(1.3, 0.4, 0.7);
  glacis.rotateX(-0.7);
  glacis.translate(0, 0.5, 1.15);
  parts.push(glacis);
  // broken tracks sagging off both sides
  for (const sx of [-1, 1]) {
    const track = new THREE.BoxGeometry(0.32, 0.3, 2.3);
    track.rotateX(sx * 0.06);
    track.translate(sx * 0.85, 0.18, -0.08);
    parts.push(track);
  }
  // turret knocked askew, cannon bent down
  const turret = new THREE.CylinderGeometry(0.55, 0.62, 0.42, 10);
  turret.rotateZ(0.35);
  turret.rotateY(0.7);
  turret.translate(0.18, 0.75, -0.3);
  parts.push(turret);
  const cannon = new THREE.CylinderGeometry(0.09, 0.11, 1.4, 7);
  cannon.rotateX(Math.PI / 2 + 0.4); // drooped toward the ground
  cannon.rotateY(0.7);
  cannon.translate(0.75, 0.62, 0.55);
  parts.push(cannon);
  // scattered plate debris
  for (const [bx, bz, ry] of [
    [-1.15, 0.9, 0.5],
    [1.2, -0.7, -0.9],
    [-0.9, -1.15, 1.1],
  ]) {
    const plate = new THREE.BoxGeometry(0.5, 0.09, 0.4);
    plate.rotateY(ry);
    plate.translate(bx, 0.05, bz);
    parts.push(plate);
  }
  return mergeGeometries(parts)!;
}

// ---------------------------------------------------------------- ironclad assault tank

/**
 * Bespoke Ironclad Assault Tank. Local +z forward. Named children: `turret` (yaws to
 * target, child `cannon` recoils on fire), `wheel0..wheel5` (roll around x).
 */
export function makeTankGroup(owner: PlayerId): THREE.Group {
  const g = new THREE.Group();
  const hullMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(0x5b636e).lerp(new THREE.Color(OWNER_COLORS[owner]), 0.18),
    metalness: 0.6,
    roughness: 0.45,
  });
  const darkMat = new THREE.MeshStandardMaterial({ color: 0x33383f, metalness: 0.5, roughness: 0.55 });
  const trimMat = new THREE.MeshStandardMaterial({ color: 0xb99a4f, metalness: 0.75, roughness: 0.35 });

  // tracks
  for (const sx of [-1, 1]) {
    const track = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.62, 3.0), darkMat);
    track.position.set(sx * 0.92, 0.42, 0);
    g.add(track);
    const fender = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.1, 3.1), hullMat);
    fender.position.set(sx * 0.92, 0.78, 0);
    g.add(fender);
  }
  // road wheels (visible roll)
  const wheelGeo = new THREE.CylinderGeometry(0.3, 0.3, 0.14, 10);
  wheelGeo.rotateZ(Math.PI / 2);
  for (let i = 0; i < 6; i++) {
    const wheel = new THREE.Mesh(wheelGeo, new THREE.MeshStandardMaterial({ color: 0x22262b, roughness: 0.7 }));
    wheel.name = `wheel${i}`;
    wheel.position.set(i % 2 === 0 ? -1.18 : 1.18, 0.32, ((i >> 1) - 1) * 1.05);
    g.add(wheel);
  }
  // hull with sloped glacis
  const hull = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.7, 2.8), hullMat);
  hull.position.y = 0.95;
  g.add(hull);
  const glacis = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.66, 0.9), hullMat);
  glacis.position.set(0, 0.86, 1.6);
  glacis.rotation.x = 0.55;
  g.add(glacis);
  // rune trim strip (magic tank flavor)
  const strip = new THREE.Mesh(new THREE.BoxGeometry(1.54, 0.08, 2.5), trimMat);
  strip.position.y = 1.32;
  g.add(strip);
  // turret + cannon
  const turret = new THREE.Group();
  turret.name = 'turret';
  const dome = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.78, 0.55, 10), hullMat);
  dome.position.y = 0.28;
  turret.add(dome);
  const cap = new THREE.Mesh(new THREE.SphereGeometry(0.55, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), darkMat);
  cap.position.y = 0.55;
  turret.add(cap);
  const cannon = new THREE.Group();
  cannon.name = 'cannon';
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.13, 1.9, 8), darkMat);
  barrel.rotation.x = Math.PI / 2;
  barrel.position.z = 0.95;
  cannon.add(barrel);
  const muzzle = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.28, 8), trimMat);
  muzzle.rotation.x = Math.PI / 2;
  muzzle.position.z = 1.85;
  cannon.add(muzzle);
  cannon.position.set(0, 0.32, 0.3);
  turret.add(cannon);
  turret.position.set(0, 1.35, -0.2);
  g.add(turret);
  // owner pennant
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 1.0, 5), darkMat);
  pole.position.set(-0.6, 2.1, -1.2);
  g.add(pole);
  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(0.45, 0.3),
    new THREE.MeshStandardMaterial({ color: OWNER_COLORS[owner], roughness: 0.8, side: THREE.DoubleSide }),
  );
  flag.position.set(-0.36, 2.45, -1.2);
  g.add(flag);

  g.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return g;
}

// ---------------------------------------------------------------- mobile ballista

/**
 * Bespoke siege engine model. Local +z is forward (matches body rotY = -facing + PI/2).
 * Named children: `wheel0..wheel3` (roll around x), `arm` (recoil pitch), `armTip` (muzzle).
 */
export function makeBallistaGroup(owner: PlayerId): THREE.Group {
  const g = new THREE.Group();
  const wood = new THREE.MeshStandardMaterial({ color: 0x7a5a3a, roughness: 0.9 });
  const woodDark = new THREE.MeshStandardMaterial({ color: 0x54402a, roughness: 0.95 });
  const metal = new THREE.MeshStandardMaterial({ color: 0x3d4148, metalness: 0.55, roughness: 0.4 });

  // chassis: two side beams + cross planks
  for (const sx of [-1, 1]) {
    const beam = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.3, 2.7), wood);
    beam.position.set(sx * 0.55, 0.62, 0);
    beam.rotation.x = -0.06;
    g.add(beam);
  }
  for (const pz of [-0.9, 0.1, 1.0]) {
    const plank = new THREE.Mesh(new THREE.BoxGeometry(1.25, 0.12, 0.34), woodDark);
    plank.position.set(0, 0.58, pz);
    g.add(plank);
  }

  // wheels (geometry axis along x so rotation.x rolls them)
  const wheelGeo = new THREE.CylinderGeometry(0.46, 0.46, 0.16, 10);
  wheelGeo.rotateZ(Math.PI / 2);
  for (let i = 0; i < 4; i++) {
    const wheel = new THREE.Mesh(wheelGeo, woodDark);
    wheel.name = `wheel${i}`;
    wheel.position.set(i % 2 === 0 ? -0.72 : 0.72, 0.46, i < 2 ? -0.85 : 0.85);
    g.add(wheel);
  }

  // throwing arm assembly (pitches back on fire)
  const arm = new THREE.Group();
  arm.name = 'arm';
  const rail = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.12, 2.3), wood);
  rail.position.z = 0.25;
  arm.add(rail);
  // bow arms swept back
  for (const sx of [-1, 1]) {
    const bow = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.1, 0.12), wood);
    bow.position.set(sx * 0.62, 0.02, 0.95);
    bow.rotation.y = sx * -0.5;
    arm.add(bow);
  }
  // string: two thin cylinders from bow tips to the nock
  for (const sx of [-1, 1]) {
    const string = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 1.15, 4), metal);
    string.position.set(sx * 0.55, 0.02, 0.45);
    string.rotation.x = Math.PI / 2;
    string.rotation.z = sx * 0.55;
    arm.add(string);
  }
  // the bolt
  const boltShaft = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 1.5, 6), woodDark);
  boltShaft.rotation.x = Math.PI / 2;
  boltShaft.position.set(0, 0.1, 0.45);
  arm.add(boltShaft);
  const boltHead = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.3, 6), metal);
  boltHead.rotation.x = Math.PI / 2;
  boltHead.position.set(0, 0.1, 1.3);
  arm.add(boltHead);
  const tip = new THREE.Object3D();
  tip.name = 'armTip';
  tip.position.set(0, 0.1, 1.4);
  arm.add(tip);
  arm.position.set(0, 0.95, -0.1);
  g.add(arm);

  // owner pennant at the rear
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 1.4, 5), woodDark);
  pole.position.set(0, 1.55, -1.25);
  g.add(pole);
  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(0.5, 0.32),
    new THREE.MeshStandardMaterial({ color: OWNER_COLORS[owner], roughness: 0.8, side: THREE.DoubleSide }),
  );
  flag.position.set(0.28, 2.05, -1.25);
  g.add(flag);

  g.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return g;
}
